import { useRouterState } from '@tanstack/react-router'

const VIEW_ROUTES = ['list', 'board', 'calendar', 'gantt'] as const
const VIEWS_ROUTE = 'views' as const

export type ChildRouteType = 'none' | 'view' | 'views' | 'other'

export interface LayoutRouteState {
  /**
   * Whether the current URL belongs to this layout (exact match or nested).
   *
   * During route transitions TanStack Router updates `location` before
   * unmounting stale child layouts.  Always check `isActive` before acting
   * on `childRouteType`.
   */
  isActive: boolean
  /** 'none' = on the bare layout path, 'view' = list/board/calendar/gantt, 'views' = saved views list/detail, 'other' = settings/pages/cycles etc. */
  childRouteType: ChildRouteType
}

/**
 * Single hook for layout route state.
 *
 * Returns `isActive` (does the URL belong to this layout at all?) together
 * with `childRouteType` so callers can't forget the stale-layout guard.
 */
export function useLayoutRouteState(basePath: string): LayoutRouteState {
  return useRouterState({
    select: (state) => {
      const pathname = state.location.pathname.replace(/\/+$/, '')
      const normalizedBase = basePath.replace(/\/+$/, '')

      if (pathname === normalizedBase) {
        return { isActive: true, childRouteType: 'none' as const }
      }

      if (pathname.startsWith(normalizedBase + '/')) {
        const suffix = pathname.slice(normalizedBase.length + 1).split('/')[0]
        let childRouteType: ChildRouteType
        if (VIEW_ROUTES.includes(suffix as (typeof VIEW_ROUTES)[number])) {
          childRouteType = 'view'
        } else if (suffix === VIEWS_ROUTE) {
          childRouteType = 'views'
        } else {
          childRouteType = 'other'
        }
        return { isActive: true, childRouteType }
      }

      return { isActive: false, childRouteType: 'none' as const }
    },
    structuralSharing: true,
  })
}
